import { ApolloQueryResult, QueryResult } from '@apollo/client';
import get from 'lodash/get';
import set from 'lodash/set';
import React from 'react';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { Field, FieldProps, FormApi, FormFooter, FormSection } from '@atlaskit/form';
import InfoIcon from '@atlaskit/icon/glyph/info';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { RadioGroup } from '@atlaskit/radio';
import SectionMessage from '@atlaskit/section-message';
import { Y200, P300 } from '@atlaskit/theme/colors';
import { PaymentMethodId, PaymentMethodPanel } from '@atlassian/commerce-payment-methods';
import {
  InfoPanel,
  NotificationTypes,
  OpenSidebarLink,
  PaymentErrorLozenge,
  isAccess,
  isResourceAccess,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { ErrorMessageOnInfoPanel, GenericErrorPage } from '@css-xp/base/error-page';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { CreditCard, PaymentMethod, PaymentMethodType, PaymentOnTerms } from '@css-xp/graphtypes';
import { FetchPaymentMethodsForTransactionAccountQuery } from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { setDefaultPaymentMethod } from '@atlassian/temp-proxies';
import { Permissions, Resources, TRANSACTION_ACCOUNT_BALANCE_PATH_2 } from '@atlassian/cssxp-typescommon';
import { ENABLE_CREDIT_MANAGEMENT } from '@atlassian/cssxp-utils-generic';
import { fetchPaymentMethodsForTransactionAccount } from '../queries/fetch-payment-methods-for-transaction-account';
import { SIDEBAR } from '../transaction-account-page';
import './set-payment-method-sidebar.scss';

interface FormData {
  paymentMethod?: PaymentMethod | null;
}

export interface PaymentMethodRequest {
  transactionAccountId: string;
  invoiceGroupId?: string;
  id: string;
}

interface Props {
  transactionAccountId: string;
  transactionAccountNumber: string | undefined | null;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
  invoiceGroupId?: string;
  isTxASummarySection?: boolean;
  isPaymentOnTermsDisabled?: boolean;
  submitAction?: (request: PaymentMethodRequest) => Promise<PaymentMethod>;
}

const { PAYMENT_METHOD_DEFAULT_WRITE } = Permissions;

export const getPaymentMethodOption = (
  paymentMethod: PaymentMethod,
  index: number,
  isDisabledPaymentMethod: boolean,
  defaultPaymentMethodId: string | undefined | null
) => {
  switch (paymentMethod.type) {
    case PaymentMethodType.CARD: {
      const creditCard = paymentMethod as CreditCard;

      // convert expMonth & expYear to Numbers
      const expMonth = +(creditCard.card?.expirationMonth ?? 0);
      const expYear = +(creditCard.card?.expirationYear ?? 0);
      return [
        {
          name: creditCard.id ?? '',
          value: creditCard.id ?? '',
          label: (
            <PaymentMethodPanel
              paymentMethod={{
                id: creditCard.id as PaymentMethodId,
                type: paymentMethod.type,
                card: {
                  brand: creditCard.card?.brand ?? '',
                  expMonth,
                  expYear,
                  last4: creditCard.card?.last4Numbers ?? '',
                  name: creditCard.card?.cardHolderName ?? '',
                },
                default: defaultPaymentMethodId === creditCard.id,
              }}
            />
          ),
          isDisabled: isDisabledPaymentMethod,
          testId: `payment-method-${index}`,
        },
      ];
    }

    case PaymentMethodType.DEFERRED: {
      const paymentOnTerms = paymentMethod as PaymentOnTerms;

      let deferredPaymentlabel = 'Payment on terms';
      deferredPaymentlabel = deferredPaymentlabel.concat(
        paymentOnTerms.terms?.invoiceDueDays ? ': due in ' + paymentOnTerms.terms.invoiceDueDays + ' days' : ''
      );
      return [
        {
          name: deferredPaymentlabel,
          label: (
            <PaymentMethodPanel
              paymentMethod={{
                id: paymentOnTerms.id as PaymentMethodId,
                type: paymentOnTerms.type,
                default: defaultPaymentMethodId === paymentOnTerms.id,
              }}
            />
          ),
          value: paymentOnTerms.id ?? '',
          isDisabled: isDisabledPaymentMethod,
          testId: `deferred-payment-method-${index}`,
        },
      ];
    }

    default: {
      return [];
    }
  }
};

export const SetPaymentMethod = ({
  transactionAccountId,
  transactionAccountNumber,
  invoiceGroupId,
  isPaymentOnTermsDisabled = false,
  isTxASummarySection = false,
  refetch,
  submitAction = setDefaultPaymentMethod,
}: Props) => {
  const { closeSidebar } = useSidebar();

  const [paymentMethodsForTxa, setPaymentMethodsForTxa] = React.useState<
    Partial<QueryResult<FetchPaymentMethodsForTransactionAccountQuery>>
  >({ loading: true });

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<PaymentMethodRequest, PaymentMethod>({
    submitFormAction: submitAction,
    onComplete: () => {
      closeSidebar();
      if (refetch) {
        refetch();
      } else {
        window.location.reload();
      }
    },
  });

  const [isEnabled] = useFeatureToggle();

  React.useEffect(() => {
    (async () => {
      const transactionAccountPaymentMethods = await graphqlClient().query({
        query: fetchPaymentMethodsForTransactionAccount,
        variables: { id: transactionAccountId },
        fetchPolicy: 'no-cache',
      });

      setPaymentMethodsForTxa({ ...transactionAccountPaymentMethods });
    })();
  }, []);

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  if (paymentMethodsForTxa.loading) return <LoadingIndicator />;
  if (paymentMethodsForTxa.error) return <GenericErrorPage error={paymentMethodsForTxa.error} />;

  const availablePaymentMethods = paymentMethodsForTxa.data?.transactionAccount?.availablePaymentMethods?.nodes ?? [];
  const accountCreditBalance = paymentMethodsForTxa.data?.transactionAccount.customerBalances?.usd ?? 0;
  const hasPushType = !!availablePaymentMethods.find(
    (paymentMethod) => paymentMethod?.type === PaymentMethodType.DEFERRED
  );
  const hasPullType = !!availablePaymentMethods.find((paymentMethod) => paymentMethod?.type === PaymentMethodType.CARD);

  const invoiceGroups = paymentMethodsForTxa.data?.transactionAccount.availableInvoiceGroups?.nodes;

  // if on an invoice group get the defaultPaymentMethod so we can display a ✅
  const invoiceGroupDefaultPaymentMethod = invoiceGroups?.find((invoiceGroup) => invoiceGroup?.id === invoiceGroupId);

  const txaDefaultPaymentMethod = availablePaymentMethods?.filter(function (paymentMethod) {
    return paymentMethod?.default;
  });

  //@ts-ignore
  const isPaymentIssue = availablePaymentMethods.some((paymentMethod) => paymentMethod?.paymentIssue === true);

  const defaultPaymentMethod = invoiceGroupId
    ? invoiceGroupDefaultPaymentMethod?.defaultPaymentMethod
    : txaDefaultPaymentMethod && txaDefaultPaymentMethod.length > 0
    ? txaDefaultPaymentMethod[0]
    : null;

  // Show only Unique Payment on terms method available and prefer to the one which is selected as default payment method in invoice group.
  const selectedPaymentOnTerm: any = [];
  let deferredPaymentMethod: PaymentMethod[] = [];

  /** Show Payment on terms option only from invoice group Page */
  if (invoiceGroupId) {
    deferredPaymentMethod =
      availablePaymentMethods
        //@ts-ignore
        ?.filter((paymentMethod: PaymentMethod) => paymentMethod.type === PaymentMethodType.DEFERRED)
        //@ts-ignore
        .map((paymentMethod: PaymentMethod) => {
          if (
            invoiceGroupDefaultPaymentMethod?.defaultPaymentMethod?.id &&
            invoiceGroupDefaultPaymentMethod?.defaultPaymentMethod?.id === paymentMethod.id
          ) {
            selectedPaymentOnTerm.push(paymentMethod);
          }
          return paymentMethod;
        }) ?? [];
  }
  const cardPaymentMethod =
    availablePaymentMethods?.filter((paymentMethod: any) => paymentMethod.type === PaymentMethodType.CARD) ?? [];

  let paymentMethods = [...selectedPaymentOnTerm, ...cardPaymentMethod];
  if (!selectedPaymentOnTerm.length && deferredPaymentMethod.length) {
    paymentMethods = [...[deferredPaymentMethod[0]], ...paymentMethods];
  }

  const submitPaymentDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });

    const setDefaultPaymentMethodPayload: PaymentMethodRequest = {
      transactionAccountId,
      id: get(transformedData, 'payment-method'),
      invoiceGroupId,
    };
    submitForm(setDefaultPaymentMethodPayload);
  };

  const disabledPaymentMethod = defaultPaymentMethod?.type === PaymentMethodType.DEFERRED;

  const defaultPaymentMethodId = defaultPaymentMethod?.id ? defaultPaymentMethod?.id : null;

  let paymentMethodOption: any[] = [];
  let defaultPaymentOption: any[] = [];
  let isDeferredPaymentAvailable = false;

  if (paymentMethods) {
    paymentMethods.forEach((paymentMethod, index) => {
      if (paymentMethod.type) {
        if (paymentMethod.type === PaymentMethodType.DEFERRED) {
          paymentMethodOption = paymentMethodOption.concat(
            getPaymentMethodOption(
              paymentMethod,
              index,
              disabledPaymentMethod || isPaymentOnTermsDisabled,
              defaultPaymentMethod?.id
            )
          );
        } else {
          paymentMethodOption = paymentMethodOption.concat(
            getPaymentMethodOption(paymentMethod, index, disabledPaymentMethod, defaultPaymentMethod?.id)
          );
        }
      }
      if (paymentMethod.type === PaymentMethodType.DEFERRED) {
        isDeferredPaymentAvailable = true;
      }
    });
  }

  let renderPaymentMethodOnTxASummary: any[] = [];

  if (paymentMethods) {
    paymentMethods.filter((paymentMethod, index) => {
      if (paymentMethod.default === true) {
        defaultPaymentOption = defaultPaymentOption.concat(
          getPaymentMethodOption(paymentMethod, index, disabledPaymentMethod, defaultPaymentMethod?.id)
        );
      }
    });
  }

  if (defaultPaymentOption) {
    renderPaymentMethodOnTxASummary = defaultPaymentOption.map((paymentMethod) => {
      return (
        <div key={paymentMethod.id} style={{ maxWidth: '250px' }}>
          {paymentMethod.label}
        </div>
      );
    });
  }

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('update-billto-party-success')
      .withHeader('Successfully updated payment method.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const paymentMethodshaveDeferredOption = (paymentMethods: any[]): boolean => {
    return paymentMethods.some((paymentMethod) => paymentMethod.type === PaymentMethodType.DEFERRED);
  };

  const getFormTitle = () => {
    if (invoiceGroupId) {
      return 'Set payment method';
    }
    return 'Default payment method';
  };

  return (
    <div className={isTxASummarySection ? 'form-body-txa-summary' : 'form-body-new'}>
      <Form onSubmit={submitPaymentDetails}>
        {({ formProps, submitting }) => (
          <form {...formProps}>
            <div className="form-header">
              {getFormTitle()}
              <span className="payment-error">{isPaymentIssue && <PaymentErrorLozenge />}</span>
            </div>
            {formError && (
              <FormSection>
                <InfoPanel
                  type="error"
                  message={
                    <ErrorMessageOnInfoPanel
                      title="Unable to update the payment method due to the following reason"
                      message={formError}
                    />
                  }
                />
              </FormSection>
            )}
            {isDeferredPaymentAvailable && (
              <SectionMessage appearance="warning">
                <p>Once payment on terms is set to default it cannot be reversed</p>
              </SectionMessage>
            )}
            {isPaymentOnTermsDisabled && paymentMethodshaveDeferredOption(paymentMethods) && (
              <div>
                <br />
                <SectionMessage appearance="warning">
                  <p>
                    Billing Profiles linked to Jira or Confluence subscriptions cannot be set to Net-30 Terms. Please
                    select an alternative payment method
                  </p>
                </SectionMessage>
              </div>
            )}

            {isTxASummarySection ? (
              <>
                {defaultPaymentMethodId ? (
                  <div className="txa-payment-method-container">
                    <Field
                      name="payment-method"
                      isRequired
                      id="payment-method"
                      defaultValue={defaultPaymentMethodId || ''}
                    >
                      {() => <div>{renderPaymentMethodOnTxASummary}</div>}
                    </Field>
                  </div>
                ) : (
                  <>
                    {!hasPullType && hasPushType && isEnabled(ENABLE_CREDIT_MANAGEMENT) ? (
                      <div key="no-payment-method" className="no-payment-method">
                        <InfoIcon primaryColor={P300} label="Warning" /> No default payment method is associated with
                        this account. Payment terms are available
                      </div>
                    ) : (
                      <div key="no-payment-method" className="no-payment-method">
                        <WarningIcon primaryColor={Y200} label="Warning" />
                        No payment method associated with this account
                      </div>
                    )}
                    {isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(PAYMENT_METHOD_DEFAULT_WRITE) && (
                      <OpenSidebarLink
                        sidebar={SIDEBAR.SET_CARD_PAYMENT_DEFAULT}
                        payload={{
                          transactionAccountId: transactionAccountId,
                          refetch: refetch,
                        }}
                        isDisabled={cardPaymentMethod.length === 0}
                        sidebarOptions={{
                          isOverlay: true,
                          sidebarWidth: '600px',
                        }}
                        label="Manage"
                      >
                        Manage
                      </OpenSidebarLink>
                    )}
                  </>
                )}
              </>
            ) : (
              <FormSection>
                <div style={{ maxWidth: '600px' }}>
                  <Field
                    name="payment-method"
                    isRequired
                    id="payment-method"
                    defaultValue={defaultPaymentMethodId || ''}
                  >
                    {({ fieldProps: { isDisabled, ...fieldProps } }: { fieldProps: FieldProps<string> }) => (
                      <RadioGroup {...fieldProps} options={paymentMethodOption} />
                    )}
                  </Field>
                </div>
              </FormSection>
            )}
            {isTxASummarySection && availablePaymentMethods?.length > 0 && (
              <div className="account-credit">
                <div className="form-header">Account balance</div>
                <div className="acb-value">USD {Math.abs(accountCreditBalance).toFixed(2)}</div>
                {accountCreditBalance === 0 ? (
                  <p className="disabled-link">view details</p>
                ) : (
                  <Link
                    to={`${TRANSACTION_ACCOUNT_BALANCE_PATH_2}/${transactionAccountNumber}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    view details
                  </Link>
                )}
              </div>
            )}
            {!isTxASummarySection && (
              <FormFooter align="start">
                <ButtonGroup>
                  <LoadingButton
                    appearance="primary"
                    type="submit"
                    isLoading={submitting || isProcessing}
                    isDisabled={disabledPaymentMethod}
                    testId={submitting || isProcessing ? 'loading-button' : 'save-button'}
                  >
                    Save
                  </LoadingButton>
                  <Button appearance="subtle" onClick={closeSidebar} testId="close">
                    Close
                  </Button>
                </ButtonGroup>
              </FormFooter>
            )}
          </form>
        )}
      </Form>
    </div>
  );
};
